import React, { useState } from 'react';
import SEO from '../components/common/SEO';
import LeadForm from '../components/LeadForm';
import {
  ArrowRight,
  CalendarCheck,
  CaretDown,
  CaretUp,
  ChartBar,
  CheckCircle,
  Clock,
  Lock,
  ShieldCheck,
  Target,
  UsersThree,
  Wrench,
  X,
} from '@phosphor-icons/react';
import { trackEvent } from '../lib/analytics';

const ACCENT = '#2563eb';
const BOOKING_URL = process.env.REACT_APP_ASSESSMENT_BOOKING_URL || '';



const scrollToForm = (label) => {
  trackEvent('cta_click', { cta_label: label, page: '/ai-process-assessment' });
  document.getElementById('assessment-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function PrimaryCTA({ location }) {
  return (
    <button className="apa-primary-cta" onClick={() => scrollToForm(location)}>
      <span>Check fit and book</span>
      <ArrowRight size={20} weight="bold" />
    </button>
  );
}

const proofPoints = [
  { icon: <Wrench size={25} weight="duotone" />, title: 'One task built live', body: 'Running on your own tools before we leave.' },
  { icon: <Lock size={25} weight="duotone" />, title: 'You drive', body: 'Your machines, your logins. We never take credentials or copies.' },
  { icon: <ShieldCheck size={25} weight="duotone" />, title: 'NDA before the session', body: 'Everything we see in your business stays confidential.' },
];

const transformations = [
  ['The same job eating your week, every week', 'One of them handed to AI and actually running'],
  ['AI tools you pay for and barely use', 'At least one real task running on them'],
  ['Ideas stuck at "we should try that sometime"', 'Built during the session, in use the next day'],
  ['Guessing where AI would even help', 'Three jobs picked from how your week really goes'],
];

const sampleRows = [
  { rank: '1', process: 'Quoting and proposals', decision: 'Built in the session', reason: 'Repeatable, high volume, clear inputs' },
  { rank: '2', process: 'Chasing invoices and payments', decision: 'Mapped, ready to build', reason: 'Good fit once the template is agreed' },
  { rank: '3', process: 'New customer admin', decision: 'Mapped', reason: 'Worth doing after the records are tidied' },
];

const agenda = [
  { time: '0:00', title: 'Walk your actual week', short: 'Where the time really goes, workarounds included.', body: 'You show us where the time goes, including the workarounds and the jobs nobody wants.' },
  { time: '0:40', title: 'Pick the three', short: 'The three worth handing over — and which AI should not touch.', body: 'We choose the three that cost you most and are realistic to hand over. We say plainly which ones AI should not touch.' },
  { time: '1:15', title: 'Build the first one', short: 'The bulk of the session. Your keyboards, your tools, us coaching.', body: 'The bulk of the session. Your people at their own keyboards, building it on your own tools, with us coaching.' },
  { time: '2:45', title: 'Map the other two', short: 'What each takes, in what order, and who picks it up.', body: 'What it takes to build each, in what order, and who on your side picks it up.' },
];

const goodFit = [
  'Something repeatable is eating hours every week',
  'The people who actually do the job can sit in',
  'Your normal tools and logins are available in the room',
  'You want it working, not a report about it',
];

const badFit = [
  'You want a general AI product demonstration',
  'The work is different every time, with nothing repeatable',
  'Nobody who does the job can be in the room',
  'You want a written strategy document rather than a working session',
];

const faqs = [
  {
    question: 'Is this really free?',
    answer: 'Yes. There is no pitch inside the session and nothing to sign afterwards. Whatever we build is yours and keeps working whether or not you ever hire Eagentix. We qualify applications because each session uses three hours of senior time.',
  },
  {
    question: 'What do we actually have at the end?',
    answer: 'One task built and running on your own tools, and the other two mapped so your team knows what building them involves. There is no written report — the point is that something works when we leave, not that you have another document to read.',
  },
  {
    question: 'Who should be in the room?',
    answer: 'Two to four people who actually do the work, plus whoever can say yes to changing how it gets done. In a smaller business that is often the same person, which is fine.',
  },
  {
    question: 'Do you need our passwords or a copy of our data?',
    answer: 'No. Your people stay at their own keyboards and log in themselves. We coach, we do not take credentials, and we do not take copies of anything away. We sign an NDA before the session.',
  },
  {
    question: 'What if AI is not the right answer?',
    answer: 'We say so, on the day. Some jobs need a process change, a proper integration or simply tidier records before any AI is worth buying. Being told that in hour one is worth more than a project that fails in month six.',
  },
  {
    question: 'Is the session onsite or remote?',
    answer: 'Onsite in Singapore and across Southeast Asia, remote elsewhere. Onsite works noticeably better, because people show you what they do instead of describing it.',
  },
];

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  const panelId = `assessment-faq-${index}`;
  return (
    <div className="apa-faq-item">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
          if (!open) trackEvent('faq_open', { question: faq.question, index, page: '/ai-process-assessment' });
        }}
      >
        <span>{faq.question}</span>
        {open ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
      </button>
      {open && <p id={panelId}>{faq.answer}</p>}
    </div>
  );
}

export default function AIProcessAssessment() {
  const bookingConfigured = Boolean(BOOKING_URL);

  return (
    <main className="apa-page">
      <SEO
        title="Free 3-Hour AI Working Session | Get Your Worst Job Off Your Plate"
        description="A free 3-hour working session. We find the three jobs eating your week, build the first one running on your own tools before we leave, and map the other two. No report, no project."
        pathname="/ai-process-assessment"
      />

      <style>{`
        .apa-page {
          --apa-ink: #0f172a;
          --apa-muted: #475569;
          --apa-line: #dbe4f0;
          --apa-soft: #f5f8fc;
          --apa-blue: ${ACCENT};
          color: var(--apa-ink);
          background: #f8fafc;
          font-family: 'General Sans', 'Inter', system-ui, sans-serif;
        }
        .apa-page * { box-sizing: border-box; }
        .apa-container { width: min(1120px, calc(100% - 48px)); margin: 0 auto; }
        .apa-section { padding: 88px 0; }
        .apa-section h2 { margin: 0 0 16px; font-size: clamp(2rem, 4vw, 3.15rem); line-height: 1.06; letter-spacing: -0.045em; }
        .apa-section-intro { max-width: 650px; margin: 0 0 42px; color: var(--apa-muted); font-size: 1.12rem; line-height: 1.65; }
        .apa-primary-cta {
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          min-height: 56px; padding: 0 26px; border: 0; border-radius: 12px;
          color: white; background: var(--apa-blue); font: inherit; font-size: 1.05rem;
          font-weight: 850; cursor: pointer; box-shadow: 0 14px 32px rgba(37, 99, 235, .32);
          transition: transform .18s ease, background-color .18s ease, box-shadow .18s ease;
          white-space: nowrap;
        }
        .apa-primary-cta:hover { background: #1d4ed8; transform: translateY(-2px); box-shadow: 0 18px 36px rgba(37, 99, 235, .36); }
        .apa-primary-cta:active { transform: translateY(1px); }
        .apa-primary-cta:focus-visible, .apa-faq-item button:focus-visible { outline: 3px solid #93c5fd; outline-offset: 3px; }

        .apa-hero { min-height: auto; display: flex; align-items: center; padding: 48px 0; overflow: hidden; background: #07101f; color: white; }
        .apa-hero-grid { display: grid; grid-template-columns: 1.05fr .95fr; align-items: center; gap: 64px; }
        .apa-eyebrow { display: block; margin-bottom: 18px; color: #93c5fd; font-size: .78rem; font-weight: 850; letter-spacing: .14em; text-transform: uppercase; }
        /* index.css sets a direct color on h1-h6, which beats the inherited
           color on dark surfaces. Restate it wherever the background is dark. */
        .apa-hero h1, .apa-hero h2, .apa-hero h3,
        .apa-proof h1, .apa-proof h2, .apa-proof h3, .apa-proof strong,
        .apa-sample-header h1, .apa-sample-header h2, .apa-sample-header h3,
        .apa-convert-copy h1, .apa-convert-copy h2, .apa-convert-copy h3 { color: #ffffff; }
        .apa-hero h1 { max-width: 640px; margin: 0 0 22px; font-size: clamp(3.1rem, 5.4vw, 4.75rem); line-height: .98; letter-spacing: -.055em; }
        .apa-hero-copy { max-width: 610px; margin: 0 0 30px; color: #cbd5e1; font-size: 1.18rem; line-height: 1.58; }
        .apa-hero-side { display: grid; gap: 18px; }
        .apa-hero-visual { overflow: hidden; border: 1px solid rgba(255,255,255,.18); border-radius: 22px; box-shadow: 0 28px 60px rgba(0,0,0,.4); }
        .apa-hero-visual img { display: block; width: 100%; height: auto; aspect-ratio: 16 / 10; object-fit: cover; }
        .apa-hero-plan { padding: 22px 26px; border: 1px solid rgba(148,163,184,.28); border-radius: 22px; background: rgba(15,23,42,.72); backdrop-filter: blur(10px); box-shadow: 0 32px 70px rgba(0,0,0,.38); }
        .apa-hero-plan-head { display: flex; align-items: center; gap: 9px; padding-bottom: 14px; margin-bottom: 2px; border-bottom: 1px solid rgba(148,163,184,.24); color: #93c5fd; font-size: .76rem; font-weight: 850; letter-spacing: .12em; text-transform: uppercase; }
        .apa-plan-list { list-style: none; margin: 0; padding: 0; counter-reset: none; }
        .apa-plan-list li { display: grid; grid-template-columns: 54px 1fr; gap: 14px; padding: 13px 0; border-bottom: 1px solid rgba(148,163,184,.16); }
        .apa-plan-list li:last-child { border-bottom: 0; padding-bottom: 0; }
        .apa-plan-time { color: #60a5fa; font-size: .98rem; font-weight: 900; font-variant-numeric: tabular-nums; line-height: 1.45; }
        .apa-plan-list strong { display: block; color: #ffffff; font-size: 1rem; font-weight: 800; line-height: 1.35; }
        .apa-plan-list li > div > span { display: block; margin-top: 4px; color: #aebed2; font-size: .89rem; line-height: 1.5; }
        .apa-hero-note { display: flex; gap: 10px; align-items: center; margin-top: 18px; color: #cbd5e1; font-size: .92rem; }

        .apa-proof { background: #0d1a2d; color: white; }
        .apa-proof-grid { display: grid; grid-template-columns: repeat(3, 1fr); }
        .apa-proof-item { display: grid; grid-template-columns: auto 1fr; gap: 14px; padding: 26px 30px; border-left: 1px solid rgba(148,163,184,.2); }
        .apa-proof-item:first-child { border-left: 0; }
        .apa-proof-item svg { color: #60a5fa; }
        .apa-proof-item strong { display: block; margin-bottom: 4px; font-size: 1rem; }
        .apa-proof-item span { color: #aebed2; font-size: .9rem; line-height: 1.5; }

        .apa-problem { background: white; }
        .apa-transformation { overflow: hidden; border: 1px solid var(--apa-line); border-radius: 20px; }
        .apa-transform-head, .apa-transform-row { display: grid; grid-template-columns: 1fr 56px 1fr; }
        .apa-transform-head > div { padding: 16px 22px; color: #64748b; background: #eef3f9; font-size: .78rem; font-weight: 850; letter-spacing: .08em; text-transform: uppercase; }
        .apa-transform-head > div:last-child { color: #1d4ed8; background: #eaf2ff; }
        .apa-transform-row { border-top: 1px solid var(--apa-line); background: white; }
        .apa-transform-row > div { padding: 20px 22px; color: #64748b; line-height: 1.5; }
        .apa-transform-row .apa-arrow { display: flex; align-items: center; justify-content: center; padding: 0; color: var(--apa-blue); }
        .apa-transform-row > div:last-child { color: var(--apa-ink); font-weight: 700; background: #f8fbff; }

        .apa-output { background: var(--apa-soft); }
        .apa-output-grid { display: grid; grid-template-columns: .8fr 1.2fr; align-items: center; gap: 64px; }
        .apa-output-copy p { color: var(--apa-muted); font-size: 1.08rem; line-height: 1.65; }
        .apa-output-copy ul { margin: 26px 0 0; padding: 0; list-style: none; display: grid; gap: 14px; }
        .apa-output-copy li { display: grid; grid-template-columns: auto 1fr; gap: 10px; color: #334155; line-height: 1.5; }
        .apa-output-copy li svg { color: var(--apa-blue); margin-top: 2px; }
        .apa-sample { overflow: hidden; border: 1px solid #cbd8e7; border-radius: 20px; background: white; box-shadow: 0 24px 55px rgba(15,23,42,.11); }
        .apa-sample-header { padding: 24px 26px; color: white; background: #0f172a; }
        .apa-sample-header small { display: block; color: #93c5fd; font-weight: 750; margin-bottom: 5px; }
        .apa-sample-header strong { font-size: 1.2rem; }
        .apa-sample-row { display: grid; grid-template-columns: 40px 1.15fr .8fr 1.15fr; gap: 14px; align-items: center; padding: 20px 24px; border-top: 1px solid #e2e8f0; }
        .apa-rank { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 8px; color: #1d4ed8; background: #dbeafe; font-weight: 850; }
        .apa-process { font-weight: 800; }
        .apa-decision { color: #1d4ed8; font-size: .88rem; font-weight: 800; }
        .apa-reason { color: #64748b; font-size: .88rem; line-height: 1.45; }
        .apa-sample-foot { padding: 14px 24px; color: #64748b; background: #f8fafc; font-size: .78rem; }


        .apa-fit { background: var(--apa-soft); }
        .apa-fit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .apa-fit-panel { padding: 34px; border: 1px solid var(--apa-line); border-radius: 18px; background: white; }
        .apa-fit-panel.good { border-color: #93c5fd; }
        .apa-fit-panel h3 { margin: 0 0 22px; font-size: 1.25rem; }
        .apa-fit-line { display: grid; grid-template-columns: auto 1fr; gap: 11px; margin-top: 14px; color: #334155; line-height: 1.5; }
        .apa-fit-panel.good svg { color: var(--apa-blue); }
        .apa-fit-panel:not(.good) svg { color: #94a3b8; }

        .apa-expert { background: white; }
        .apa-expert-grid { display: grid; grid-template-columns: 240px 1fr; gap: 52px; align-items: center; max-width: 900px; }
        .apa-expert img { display: block; width: 240px; height: 300px; object-fit: cover; border-radius: 18px; box-shadow: 0 20px 45px rgba(15,23,42,.16); }
        .apa-expert h2 { margin-bottom: 8px; }
        .apa-role { color: var(--apa-blue); font-weight: 800; }
        .apa-expert p { color: var(--apa-muted); line-height: 1.65; }
        .apa-expert-facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 24px; }
        .apa-expert-fact { padding: 16px; border-radius: 12px; background: var(--apa-soft); }
        .apa-expert-fact strong { display: block; font-size: 1.1rem; }
        .apa-expert-fact span { color: #64748b; font-size: .82rem; }

        .apa-faq { background: var(--apa-soft); }
        .apa-faq-wrap { max-width: 840px; }
        .apa-faq-item { border-bottom: 1px solid #cbd5e1; }
        .apa-faq-item button { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 22px 0; border: 0; color: var(--apa-ink); background: transparent; font: inherit; font-size: 1.08rem; font-weight: 800; text-align: left; cursor: pointer; }
        .apa-faq-item button svg { flex: 0 0 auto; color: var(--apa-blue); }
        .apa-faq-item p { max-width: 760px; margin: -2px 0 22px; color: var(--apa-muted); line-height: 1.65; }

        .apa-convert { background: white; }
        .apa-convert-grid { display: grid; grid-template-columns: .85fr 1.15fr; gap: 64px; align-items: start; }
        .apa-convert-copy { position: sticky; top: 110px; padding: 38px; border-radius: 20px; color: white; background: #0f172a; }
        .apa-convert-copy h2 { color: white; }
        .apa-convert-copy p { color: #cbd5e1; font-size: 1.05rem; line-height: 1.65; }
        .apa-convert-points { display: grid; gap: 14px; margin-top: 26px; }
        .apa-convert-point { display: grid; grid-template-columns: auto 1fr; gap: 11px; color: #e2e8f0; line-height: 1.5; }
        .apa-convert-point svg { color: #60a5fa; margin-top: 2px; }
        .apa-convert-form form { max-width: none !important; margin: 0 !important; border: 1px solid var(--apa-line); box-shadow: 0 22px 52px rgba(15,23,42,.09) !important; }
        .apa-privacy { margin: 18px 0 0; color: #64748b; font-size: .88rem; line-height: 1.55; text-align: center; }

        @media (max-width: 900px) {
          .apa-hero { min-height: auto; padding: 64px 0; }
          .apa-hero-grid, .apa-output-grid, .apa-convert-grid { grid-template-columns: 1fr; }
          .apa-hero-grid { gap: 42px; }
          .apa-hero-side { max-width: 620px; }
          .apa-proof-grid { grid-template-columns: 1fr; }
          .apa-proof-item { border-left: 0; border-top: 1px solid rgba(148,163,184,.2); }
          .apa-proof-item:first-child { border-top: 0; }
          .apa-convert-copy { position: static; }
        }
        @media (max-width: 640px) {
          .apa-container { width: min(100% - 32px, 1120px); }
          .apa-section { padding: 64px 0; }
          .apa-hero { padding: 42px 0 48px; }
          .apa-hero h1 { font-size: 2.55rem; line-height: 1.02; }
          .apa-hero-plan { padding: 24px 22px; }
          .apa-plan-list li { grid-template-columns: 50px 1fr; gap: 12px; padding: 15px 0; }
          .apa-hero-copy { font-size: 1.05rem; line-height: 1.55; }
          .apa-primary-cta { width: 100%; }
          .apa-hero-note { font-size: .85rem; }
          .apa-transform-head { grid-template-columns: 1fr 1fr; }
          .apa-transform-head > div:nth-child(2) { display: none; }
          .apa-transform-row { grid-template-columns: 1fr; }
          .apa-transform-row .apa-arrow { padding: 8px 20px; justify-content: flex-start; transform: rotate(90deg); }
          .apa-sample-row { grid-template-columns: 34px 1fr; }
          .apa-decision, .apa-reason { grid-column: 2; }
          .apa-fit-grid { grid-template-columns: 1fr; }
          .apa-expert-grid { grid-template-columns: 1fr; gap: 30px; }
          .apa-expert img { width: 180px; height: 220px; }
          .apa-expert-facts { grid-template-columns: 1fr; }
          .apa-convert-copy, .apa-fit-panel { padding: 26px; }
          .apa-convert-form form { padding: 28px 22px !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .apa-page * { scroll-behavior: auto !important; transition: none !important; }
        }
      `}</style>

      <section className="apa-hero">
        <div className="apa-container apa-hero-grid">
          <div>
            <span className="apa-eyebrow">Free 3-hour working session</span>
            <h1>Get your worst weekly job off your plate.</h1>
            <p className="apa-hero-copy">In three hours we find the three jobs eating your week, build the first one running on your own tools, and map the other two. You leave with something working, not a report.</p>
            <PrimaryCTA location="Hero Check Fit" />
            <div className="apa-hero-note">
              <CalendarCheck size={19} weight="duotone" />
              <span>For teams of 10 to 500. Choose a session time once your application is confirmed.</span>
            </div>
          </div>
          <div className="apa-hero-side">
            <div className="apa-hero-visual">
              <img
                src="/images/ai-process-assessment-founder-workshop.webp"
                alt="Abhijit Ghosh walking a client team through their process map in a private working session"
                width="640"
                height="400"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="apa-hero-plan">
            <div className="apa-hero-plan-head">
              <Clock size={18} weight="duotone" aria-hidden="true" />
              <span>How the three hours run</span>
            </div>
            <ol className="apa-plan-list">
              {agenda.map((item) => (
                <li key={item.time}>
                  <span className="apa-plan-time">{item.time}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.short}</span>
                  </div>
                </li>
              ))}
            </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="apa-proof" aria-label="Assessment commitments">
        <div className="apa-container apa-proof-grid">
          {proofPoints.map((item) => (
            <div className="apa-proof-item" key={item.title}>
              {item.icon}
              <div><strong>{item.title}</strong><span>{item.body}</span></div>
            </div>
          ))}
        </div>
      </section>

      <section className="apa-section apa-problem">
        <div className="apa-container">
          <h2>The job is costing you more than the software would.</h2>
          <p className="apa-section-intro">Most businesses do not need another AI tool. They need one of the jobs that eats every week to stop needing a person.</p>
          <div className="apa-transformation">
            <div className="apa-transform-head"><div>Today</div><div aria-hidden="true" /><div>After the session</div></div>
            {transformations.map(([before, after]) => (
              <div className="apa-transform-row" key={before}>
                <div>{before}</div><div className="apa-arrow"><ArrowRight size={20} weight="bold" /></div><div>{after}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="apa-section apa-output" id="sample-output">
        <div className="apa-container apa-output-grid">
          <div className="apa-output-copy">
            <h2>Leave with it working, not written up.</h2>
            <p>By hour three one job is running on your own tools and your team has seen exactly how it was built. The other two are mapped so they know what comes next.</p>
            <ul>
              <li><Target size={20} weight="fill" /><span>The three jobs costing you the most time each week</span></li>
              <li><Wrench size={20} weight="fill" /><span>The first one built and running on your own tools</span></li>
              <li><ChartBar size={20} weight="fill" /><span>The other two mapped, with what building them takes and who picks it up</span></li>
            </ul>
          </div>
          <div className="apa-sample" aria-label="Illustrative assessment output">
            <div className="apa-sample-header"><small>Illustrative</small><strong>What the three look like by hour three</strong></div>
            {sampleRows.map((row) => (
              <div className="apa-sample-row" key={row.rank}>
                <span className="apa-rank">{row.rank}</span>
                <span className="apa-process">{row.process}</span>
                <span className="apa-decision">{row.decision}</span>
                <span className="apa-reason">{row.reason}</span>
              </div>
            ))}
            <div className="apa-sample-foot">Example only. Yours come from your own week, not a template.</div>
          </div>
        </div>
      </section>

      <section className="apa-section apa-fit">
        <div className="apa-container">
          <h2>Who this works for.</h2>
          <p className="apa-section-intro">Teams of roughly 10 to 500. A clear fit protects your time and ours — if we cannot get something working in three hours, we would rather say so on the call.</p>
          <div className="apa-fit-grid">
            <div className="apa-fit-panel good">
              <h3>Good fit</h3>
              {goodFit.map((item) => <div className="apa-fit-line" key={item}><CheckCircle size={20} weight="fill" /><span>{item}</span></div>)}
            </div>
            <div className="apa-fit-panel">
              <h3>Not a fit</h3>
              {badFit.map((item) => <div className="apa-fit-line" key={item}><X size={20} weight="bold" /><span>{item}</span></div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="apa-section apa-expert">
        <div className="apa-container apa-expert-grid">
          <img src="/images/abhijit-ghosh.jpg" alt="Abhijit Ghosh, Founder of Eagentix" />
          <div>
            <h2>Run by a practitioner.</h2>
            <div className="apa-role">Abhijit Ghosh, Founder of Eagentix</div>
            <p>Abhijit has spent 25 years building data and enterprise systems across Accenture, IBM, Deloitte, Cloudera, Tibco and Wipro. He runs the session himself, and he will tell you when AI is the wrong answer.</p>
            <div className="apa-expert-facts">
              <div className="apa-expert-fact"><strong>25 years</strong><span>enterprise delivery</span></div>
              <div className="apa-expert-fact"><strong>5 sectors</strong><span>including regulated work</span></div>
              <div className="apa-expert-fact"><strong>1 job</strong><span>working before we leave</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="apa-section apa-faq">
        <div className="apa-container apa-faq-wrap">
          <h2>Questions before you apply</h2>
          {faqs.map((faq, index) => <FAQItem faq={faq} index={index} key={faq.question} />)}
        </div>
      </section>

      <section className="apa-section apa-convert" id="assessment-form">
        <div className="apa-container apa-convert-grid">
          <div className="apa-convert-copy">
            <span className="apa-eyebrow">By application</span>
            <h2>Check fit and book.</h2>
            <p>Tell us enough to see whether we can get something working in three hours. If it fits, you pick a session time next.</p>
            <div className="apa-convert-points">
              <div className="apa-convert-point"><Clock size={20} weight="duotone" /><span>Three-hour private working session</span></div>
              <div className="apa-convert-point"><UsersThree size={20} weight="duotone" /><span>Two to four people who do the job, plus whoever can approve a change</span></div>
              <div className="apa-convert-point"><Wrench size={20} weight="duotone" /><span>One job built and running on your own tools</span></div>
              <div className="apa-convert-point"><ShieldCheck size={20} weight="duotone" /><span>NDA signed before the session</span></div>
            </div>
          </div>
          <div className="apa-convert-form">
            <LeadForm
              page="/ai-process-assessment"
              showJobTitle
              showTeamSize
              jobTitleLabel="Role"
              teamSizeLabel="Team size"
              teamSizeOptions={['1-10', '11-49', '50-199', '200-500']}
              title="Check fit and book"
              subtitle="Three required fields. Nothing to prepare."
              buttonLabel="Check fit and book"
              messageLabel="Which job eats the most time? (optional)"
              messagePlaceholder="Example: quoting takes me most of Thursday, every week"
              consentLabel="Send me occasional Eagentix insights and service updates."
              successText={bookingConfigured
                ? 'Your request is recorded. Choose a session time now.'
                : 'Your request is recorded. We will confirm fit and send available times within one business day.'}
              successActionUrl={BOOKING_URL}
              successActionLabel="Choose an assessment time"
            />
            <p className="apa-privacy">We use your details to respond to this request. Marketing updates are optional.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
